import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import Modal from 'components/Modal/Modal';
import { Link, Divider, Text } from 'components/ui';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import { localeSelector } from 'viewModel/selectors/locale/localeSelector';
import productActions from 'actions/ProductActions';
import HighlightedReviews from 'utils/HighlightedReviews';
import UI from 'utils/UI';
import localeUtils from 'utils/LanguageLocale';
import SentimentModalEN from 'components/ProductPage/RatingsAndReviews/HighlyRatedFor/SentimentModal/locales/SentimentModal_en_US';
import SentimentModalFR from 'components/ProductPage/RatingsAndReviews/HighlyRatedFor/SentimentModal/locales/SentimentModal_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(SentimentModalEN, SentimentModalFR);
const { highlightRange } = HighlightedReviews;

const LIMIT_OF_REVIEWS = 6;
const PAGE = 0;

const SentimentModal = ({
    proSentiments, isModalOpen = false, closeModal, productId, onSentimentApply
}) => {
    const dispatch = useDispatch();
    const language = useSelector(localeSelector);
    const localization = { seeMore: getText('seeMore'), modalTitle: getText('modalTitle') };

    const loadHighlightedReviews = (...args) => {
        if (UI.isIOS()) {
            UI.unlockBackgroundPosition();
        }

        UI.scrollTo({ elementId: 'ratings-reviews-container' });
        dispatch(productActions.loadHighlightedReviews(...args));
    };

    const fireAnalytics = (proConType = '', sentiment = '') => {
        const actionLink = 'reviews:ratings&reviews-filter:highly rated';
        const selectedFilter = proConType.replace(/([A-Z])/g, ' $1') + '=' + sentiment;
        const pageDetail = 'ratings&reviews-highly rated modal';
        const pageType = 'reviews';
        const pageWorld = 'n/a';
        const pageName = `${pageType}:${pageDetail}:${pageWorld}:*`;

        const eventData = {
            data: {
                filterSelections: selectedFilter,
                selectedFilter: selectedFilter,
                actionInfo: actionLink,
                linkName: actionLink,
                eventStrings: anaConsts.Event.EVENT_71,
                pageDetail,
                pageType,
                world: pageWorld,
                pageName,
                previousPage: pageName
            }
        };

        pdpAnalytics.trackLinkClick(eventData.data);
    };

    return (
        <Modal
            width={0}
            isOpen={isModalOpen}
            onDismiss={closeModal}
        >
            <Modal.Header>
                <Modal.Title>{localization.modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body lineHeight='tight'>
                {proSentiments.map((pro, index) => (
                    <React.Fragment key={`${index}_${pro}`}>
                        {index !== 0 ? <Divider marginY={4} /> : null}
                        <Text
                            is='h3'
                            fontWeight='bold'
                            children={pro.sentiment}
                        />
                        {pro.representativeQuote?.quote && (
                            <Text
                                is='p'
                                marginTop={1}
                                marginBottom={2}
                                dangerouslySetInnerHTML={{
                                    __html: highlightRange({
                                        quote: pro.representativeQuote?.quote,
                                        ranges: pro.representativeQuote?.highLights
                                    })
                                }}
                            />
                        )}
                        <Link
                            color='blue'
                            padding={2}
                            margin={-2}
                            children={`${localization.seeMore} ${pro.sentiment} (${pro.count})`}
                            onClick={() => {
                                onSentimentApply();
                                loadHighlightedReviews({
                                    sentiment: pro.sentiment,
                                    type: pro.type,
                                    productId,
                                    limit: LIMIT_OF_REVIEWS,
                                    page: PAGE,
                                    language
                                });
                                fireAnalytics(pro.type, pro.sentiment);
                                closeModal();
                            }}
                        />
                    </React.Fragment>
                ))}
            </Modal.Body>
        </Modal>
    );
};

export default wrapFunctionalComponent(SentimentModal, 'SentimentModal');
